/**
 * LinkEmailToJobDialog — search-and-pick UI for linking an inbox email to
 * a job (CSR command-centre manual-link work, J4 mirror). The inverse of
 * ``AttachEmailDialog`` (work-units/AttachEmailDialog.tsx): that dialog
 * searches a job's customer's *emails* to attach one; this dialog searches
 * an email's customer's *jobs* to link it. Opened from
 * ``CommunicationTimeline``'s per-row "Link to a job…" action on email
 * rows.
 *
 * The job search reuses ``DataAdapter.getJobsForIssuePicker`` — an
 * existing, contract-tested read already wired to both the mock and real
 * adapters (built for RaiseIssuePage's and TicketModal's job-link
 * pickers). It is a customer-scoped "search this customer's jobs" read,
 * which is exactly what this picker needs too, so no new backend surface
 * or mock-only fixture was introduced for this journey. The real route
 * (``GET /views/jobs-for-issues``) is gated server-side by
 * ``Permit.WORK_VISIBILITY_VIEW`` rather than ``Permit.ISSUES_RAISE`` —
 * re-gated at productionisation because the read is generic ("list a
 * customer's jobs for a picker") and now serves both issue-raising and
 * this comms-linking journey, not just issue-raising.
 *
 * Unlike ``AttachEmailDialog`` (which delegates the write to its caller so
 * the caller can optimistically splice the new row into a list it owns),
 * this dialog owns the ``linkEmailToJob`` write itself: the communications
 * feed has no "linked to job" field on a row to reconcile, so there is no
 * caller-owned list state to update optimistically. The caller (an
 * `onLinked` callback) only needs to know the write finished, not what to
 * do with a new row.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { faro } from "@grafana/faro-web-sdk";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { IssuePickerJob } from "@shared-core/data/api/issue-pickers";

/** Minimal identity of the email being linked — enough to perform the
 *  write and to describe it in the dialog header. */
export interface EmailToLink {
  id: string;
  subject: string;
  from_name: string;
  from_address: string;
}

interface LinkEmailToJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The email's customer — scopes the job search to this customer. */
  customerId: string;
  email: EmailToLink;
  /** Called after a successful link write, once the dialog has closed. */
  onLinked?: () => void;
  /** Pre-seeds the "linking" row for Storybook — not used on the page. */
  initialLinkingJobNo?: string;
}

const SEARCH_DEBOUNCE_MS = 250;
const RESULTS_PAGE_SIZE = 20;

export function LinkEmailToJobDialog({
  open,
  onOpenChange,
  customerId,
  email,
  onLinked,
  initialLinkingJobNo,
}: LinkEmailToJobDialogProps) {
  const dataAdapter = useDataAdapter();
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<IssuePickerJob[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkingJobNo, setLinkingJobNo] = useState<string | null>(
    initialLinkingJobNo ?? null,
  );
  const fetchSeq = useRef(0);

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++fetchSeq.current;
      setLoading(true);
      setError(null);
      try {
        const result = await dataAdapter.getJobsForIssuePicker(customerId, {
          q: q || undefined,
          pageSize: RESULTS_PAGE_SIZE,
        });
        if (seq !== fetchSeq.current) return;
        setCandidates(result.items);
      } catch (err) {
        if (seq !== fetchSeq.current) return;
        setError(err instanceof Error ? err.message : "Search failed");
        setCandidates([]);
      } finally {
        if (seq === fetchSeq.current) setLoading(false);
      }
    },
    [dataAdapter, customerId],
  );

  // Reset and load the customer's most recent jobs as soon as the dialog opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCandidates(null);
    setLinkingJobNo(initialLinkingJobNo ?? null);
    void runSearch("");
    // Only on open — the debounced effect below handles subsequent typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced re-search while the dialog stays open and the query changes.
  useEffect(() => {
    if (!open || candidates === null) return;
    const handle = setTimeout(
      () => void runSearch(query.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function handleSelect(job: IssuePickerJob) {
    if (linkingJobNo) return;
    setLinkingJobNo(job.job_no);
    try {
      await dataAdapter.linkEmailToJob(email.id, job.job_no);
      toast.success(`Email linked to job #${job.job_no}`);
      onLinked?.();
    } catch (err) {
      toast.error("Couldn't link this email — try again.");
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("communications.link_email_to_job.failed"),
        { context: { email_id: email.id, job_no: job.job_no } },
      );
    } finally {
      setLinkingJobNo(null);
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 overflow-hidden sm:max-w-lg">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>Link to a job</DialogTitle>
          <DialogDescription>
            Search this customer&apos;s jobs and pick one to link &quot;
            {email.subject}&quot; to.
          </DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by job number or title…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-[360px]">
            {loading && (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Searching…
              </div>
            )}
            {error && !loading && (
              <div className="px-4 py-6 text-sm text-destructive">{error}</div>
            )}
            {!loading && !error && candidates && candidates.length === 0 && (
              <CommandEmpty>
                No jobs found{query.trim() ? ` for "${query.trim()}"` : ""}.
              </CommandEmpty>
            )}
            {!loading && !error && candidates && candidates.length > 0 && (
              <CommandGroup>
                {candidates.map((job) => {
                  const isLinking = linkingJobNo === job.job_no;
                  return (
                    <CommandItem
                      key={job.job_no}
                      value={job.job_no}
                      disabled={linkingJobNo !== null}
                      onSelect={() => void handleSelect(job)}
                      className="flex-col items-stretch gap-0.5 py-2"
                    >
                      <span className="font-medium truncate">
                        #{job.job_no}
                      </span>
                      <span className="text-sm text-muted-foreground truncate">
                        {job.title}
                      </span>
                      {isLinking && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Loader2 className="size-3 animate-spin" />
                          Linking…
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
